/**
 * Shared Cloudflare Proxy V3 staging version helpers (Story + Invoice NavMenu).
 *
 * Resolve `clientVer` / `serverVer` for menu paths so canary routing stays
 * sticky after in-app navigation (non-production only).
 *
 * Sources (per key, first wins):
 * 1. Current URL query (`?clientVer=` / `?serverVer=`)
 * 2. Document head meta injected by CF Proxy V3:
 *    `<meta name="x-client-version">` / `<meta name="x-server-version">`
 *
 * Aligns with Invoice BE `StagingProxyVersion` sanitize rules.
 *
 * @fordeer-rule staging-proxy-versions-header-and-query
 * @fordeer-rule cloudflare-proxy-v3-staging-pr
 */

/** Query keys CF Proxy V3 reads for UI/BE canary. */
export const STAGING_PROXY_VERSION_QUERY_KEYS = [
  'clientVer',
  'serverVer',
] as const

export type StagingProxyVersionQueryKey =
  (typeof STAGING_PROXY_VERSION_QUERY_KEYS)[number]

/**
 * Meta `name` attributes CF Proxy V3 injects into HTML head.
 * IMPORTANT: Keep in sync with cloudflare-proxy-v3 metaTags.
 */
export const STAGING_PROXY_VERSION_META_NAMES: Record<
  StagingProxyVersionQueryKey,
  string
> = {
  clientVer: 'x-client-version',
  serverVer: 'x-server-version',
}

/**
 * Allow `main` or light numeric/semver tokens only — reject inject payloads.
 * Mirrors Invoice `StagingProxyVersion::SANITIZE_PATTERN`.
 */
const STAGING_PROXY_VERSION_PATTERN = /^(?:main|[\d.]+)$/i

/**
 * Sanitize a staging proxy version token from query or meta content.
 *
 * @param raw - Untrusted value
 * @returns Sanitized token or null when blank/invalid
 */
export const sanitizeStagingProxyVersion = (
  raw: string | null | undefined,
): string | null => {
  if (raw == null) return null
  const trimmed = String(raw).trim()
  if (!trimmed) return null
  return STAGING_PROXY_VERSION_PATTERN.test(trimmed) ? trimmed : null
}

/**
 * Read a single sanitized version from document head meta.
 *
 * @param metaName - Meta `name` attribute (e.g. `x-client-version`)
 * @param doc - Document to query (tests inject a stub)
 * @returns Sanitized content or null
 */
export const readStagingProxyVersionMeta = (
  metaName: string,
  doc: Pick<Document, 'querySelector'> | null | undefined = typeof document !==
  'undefined'
    ? document
    : null,
): string | null => {
  if (!doc) return null
  const el = doc.querySelector(`meta[name="${metaName}"]`)
  const content =
    el && 'getAttribute' in el
      ? el.getAttribute('content')
      : (el as { content?: string } | null)?.content
  return sanitizeStagingProxyVersion(content)
}

export type StagingProxyVersionQueryOptions = {
  /** Override `window.location.search` (tests / React Router `location.search`). */
  search?: string
  /**
   * Override env gate. Default: `VITE_VERCEL_ENV !== 'production'`.
   */
  isNonProduction?: boolean
  /**
   * Document used for meta fallback. Default: global `document`.
   * Pass `null` in Node tests to disable meta reads.
   */
  document?: Pick<Document, 'querySelector'> | null
  /**
   * Pre-resolved meta versions (tests). When set, skips `document` lookup
   * for keys that have a defined value (including null = absent).
   */
  metaVersions?: Partial<Record<StagingProxyVersionQueryKey, string | null>>
}

/**
 * Resolve one version key: URL query first, then head meta / override.
 */
const resolveStagingProxyVersion = (
  key: StagingProxyVersionQueryKey,
  fromUrl: URLSearchParams,
  options: StagingProxyVersionQueryOptions,
): string | null => {
  const fromQuery = sanitizeStagingProxyVersion(fromUrl.get(key))
  if (fromQuery) return fromQuery

  if (options.metaVersions && key in options.metaVersions) {
    return sanitizeStagingProxyVersion(options.metaVersions[key])
  }

  const doc =
    options.document !== undefined
      ? options.document
      : typeof document !== 'undefined'
        ? document
        : null

  return readStagingProxyVersionMeta(STAGING_PROXY_VERSION_META_NAMES[key], doc)
}

/**
 * Append sanitized `clientVer` / `serverVer` onto a menu path.
 *
 * Production: identity. Staging/preview/dev: merge versions from URL query
 * and/or CF Proxy meta tags. Does not invent versions when both sources empty.
 *
 * IMPORTANT: Keep navigation route path/href constants clean; call this only
 * when building menu `href` / `navigate` targets.
 *
 * @param path - Clean pathname (optionally with unrelated query)
 * @param options - Inject search / env / meta for tests
 */
export const withStagingProxyVersionQuery = (
  path: string,
  options: StagingProxyVersionQueryOptions = {},
): string => {
  const isNonProduction =
    options.isNonProduction ??
    // Optional chain: Node unit tests may lack Vite `import.meta.env`.
    (import.meta as ImportMeta & { env?: { VITE_VERCEL_ENV?: string } }).env
      ?.VITE_VERCEL_ENV !== 'production'

  if (!isNonProduction) return path

  const search =
    options.search ??
    (typeof window !== 'undefined' ? window.location.search : '')

  const fromUrl = new URLSearchParams(
    search
      ? search.startsWith('?')
        ? search.slice(1)
        : search
      : '',
  )

  const versions = new URLSearchParams()
  for (const key of STAGING_PROXY_VERSION_QUERY_KEYS) {
    const resolved = resolveStagingProxyVersion(key, fromUrl, options)
    if (resolved) versions.set(key, resolved)
  }
  // Prefer toString() over iterator spread — stays ES-friendly for tsc targets.
  if (!versions.toString()) return path

  const qIndex = path.indexOf('?')
  const pathname = qIndex === -1 ? path : path.slice(0, qIndex)
  const existingQuery = qIndex === -1 ? '' : path.slice(qIndex + 1)
  const merged = new URLSearchParams(existingQuery)

  versions.forEach((value, key) => {
    // Prefer values already on the target path; otherwise copy from location/meta.
    if (!merged.has(key)) merged.set(key, value)
  })

  const qs = merged.toString()
  return qs ? `${pathname}?${qs}` : pathname
}
